//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ITwitterSettingsState } from '@account-settings/redux/twitter/twitterSettingsModel';
import * as twitterApi from '@apis/twitter-settings/twitterSettingsApi';
import { ITwitterSettingsResponse } from '@apis/twitter-settings/twitterSettingsApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';

import gaApi from 'app/initialization/ga/ga-api';

const initialState: ITwitterSettingsState = {
  errors: {
    fetchSettingsError: undefined,
    connectError: undefined,
    disconnectError: undefined,
  },
  flags: {
    hasFetchedSettings: false,
    isFetchingSettings: false,
    isConnecting: false,
    isDisconnecting: false,
  },
  isAccountConnected: false,
  username: undefined,
  oauthEndpoint: undefined,
};

const { actions, reducer } = createSlice({
  name: SliceNames.TwitterSettings,
  initialState,
  reducers: {
    requestSettings(state) {
      state.errors.fetchSettingsError = undefined;
      state.flags.isFetchingSettings = true;
      state.flags.hasFetchedSettings = false;
    },
    receiveSettingsSucceeded(
      state,
      action: PayloadAction<ITwitterSettingsResponse>,
    ) {
      const { account } = action.payload;

      state.isAccountConnected = isDefined(account);
      state.username = account?.username;
      state.flags.isFetchingSettings = false;
      state.flags.hasFetchedSettings = true;
    },
    receiveSettingsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingSettings = false;
      state.errors.fetchSettingsError = action.payload;
      state.flags.hasFetchedSettings = false;
    },
    requestTwitterOAuthEndpoint(state) {
      state.errors.connectError = undefined;
      state.flags.isConnecting = true;
    },
    receiveTwitterOAuthEndpointSucceeded(state, action: PayloadAction<string>) {
      state.oauthEndpoint = action.payload;
      state.flags.isConnecting = false;
    },
    receiveTwitterOAuthEndpointFailed(state, action: PayloadAction<string>) {
      state.flags.isConnecting = false;
      state.errors.connectError = action.payload;
    },
    requestDeAuthTwitter(state) {
      state.errors.disconnectError = undefined;
      state.flags.isDisconnecting = true;
    },
    receiveDeAuthTwitterSucceeded(state) {
      state.isAccountConnected = false;
      state.username = undefined;

      state.flags.isDisconnecting = false;
    },
    receiveDeAuthTwitterFailed(state, action: PayloadAction<string>) {
      state.flags.isDisconnecting = false;
      state.errors.disconnectError = action.payload;
    },
  },
});

export const fetchTwitterSettings = createThunk(() => async (dispatch) => {
  dispatch(actions.requestSettings());
  try {
    const settings = await twitterApi.getTwitterSettings();
    dispatch(actions.receiveSettingsSucceeded(settings));
    return settings.account?.username;
  } catch (_e) {
    dispatch(
      actions.receiveSettingsFailed(
        'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

export const connectTwitter = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTwitterOAuthEndpoint());
  try {
    const response = await twitterApi.getTwitterOAuthEndpoint();
    dispatch(actions.receiveTwitterOAuthEndpointSucceeded(response.url));
  } catch (_e) {
    dispatch(
      actions.receiveTwitterOAuthEndpointFailed(
        'An error occurred when trying to connect your X account.',
      ),
    );
  }
});

export const disconnectTwitter = createThunk(
  (username: string) => async (dispatch) => {
    dispatch(actions.requestDeAuthTwitter());
    try {
      await twitterApi.removeTwitterV2Mineables();
      await twitterApi.deauthTwitter(username);
      dispatch(actions.receiveDeAuthTwitterSucceeded());
      gaApi.recordEvent(
        gaApi.CATEGORY_SETTINGS,
        gaApi.ACTION_TWITTER_DISCONNECT_ACCOUNT,
        gaApi.LABEL_SUCCESS,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'X account successfully disconnected!',
          type: ToastType.SUCCESS,
        }),
      );
    } catch (_e) {
      const errorMessage =
        'An error occurred when trying to disconnect your X account.';
      dispatch(actions.receiveDeAuthTwitterFailed(errorMessage));
      gaApi.recordEvent(
        gaApi.CATEGORY_SETTINGS,
        gaApi.ACTION_TWITTER_DISCONNECT_ACCOUNT,
        gaApi.LABEL_FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: errorMessage,
          type: ToastType.ERROR,
        }),
      );
    }
  },
);

export default reducer;
